/**
 * Tutorials — the learning library: how-it-works videos agents can come back to
 * at any time. /tutorials is the library (search, topics, continue watching),
 * /tutorials/:id plays one video with its chapters.
 *
 * What's new says what changed in one release and then moves down the history;
 * a tutorial explains how something works, for as long as it works that way.
 * They are released by the developer with the app, like the release notes: add an
 * entry here and its two files under public/tutorial-videos/ (the .mp4 stays out
 * of git like the What's new clips; the .jpg poster is committed).
 *
 * Agent videos only. The admin portal lists these too, as the agent's view, next
 * to HQ's own (cid_admin_portal/src/utils/tutorials.js): a new agent video goes in
 * both files. Every agent, client and property in a tutorial must be a demo:
 * never a real agent's deal or income.
 *
 * Agent-facing copy: what they see and do, not how it is built.
 * Each entry:
 *   id        the URL (/tutorials/<id>); never change it once released
 *   topic     the shelf it sits on; topics are listed in order of first appearance
 *   title, summary
 *   length    m:ss, as shown on the card
 *   added     YYYY-MM-DD; New for 30 days, newest first within a topic
 *   video     { src, poster }
 *   chapters  [{ at (seconds), title, text }] — where each part starts; a chapter
 *             is a jump in the player, so take `at` from the video itself
 *   note      optional, shown under the summary
 */
export const TUTORIALS_PATH = '/tutorials';
export const tutorialPath = (id) => `${TUTORIALS_PATH}/${encodeURIComponent(id)}`;

export const TUTORIALS = [
  {
    id: 'co-share',
    topic: 'Sales',
    title: 'Co-share: your buyer, another agent’s listing',
    summary: 'You have a buyer for a property another CID agent is listing. Ask to co-share it and agree the split; the listing agent raises one sale with you both on it, and you both sign the Internal Co-Share Form in CIDAS.',
    length: '7:34',
    added: '2026-10-14',
    video: { src: '/tutorial-videos/co-share.mp4', poster: '/tutorial-videos/co-share.jpg' },
    chapters: [
      { at: 10, title: 'Ask', text: 'Open the listing from Listings → Others’ listings and tap Request co-share. Say what you have and the share you want.' },
      { at: 83, title: 'The answer', text: 'The listing agent is notified, reads your request and accepts or declines it. They can change the share when they accept.' },
      { at: 153, title: 'Accepted', text: 'You are notified, and you can now see the unit address.' },
      { at: 182, title: 'The sale', text: 'The listing agent taps Create sale from this co-share. The listing’s details come filled in, and you are already in the split.' },
      { at: 274, title: 'In your Sales list', text: 'The sale shows in your own Sales list as well.' },
      { at: 291, title: 'The form', text: 'The listing agent generates the Internal Co-Share Form from the sale’s Attachments, issues it and signs it.' },
      { at: 391, title: 'You sign', text: 'You sign it on your phone, and it shows as signed by both of you.' },
      { at: 437, title: 'In short', text: 'The whole flow on one card.' },
    ],
    note: 'Either agent can withdraw a request. More → Co-share lists every request you sent or received. If the split changes after you have both signed, the form is reissued and signed again.',
  },
  {
    id: 'cid-forms',
    topic: 'Documents',
    title: 'Get HQ’s documents in CID Forms',
    summary: 'HQ shares forms, checklists and guides as PDF, Word or Excel files. Find them in CID Forms and download them on your phone or on the web; a PDF can be read right in the app.',
    length: '2:59',
    added: '2026-10-23',
    video: { src: '/tutorial-videos/cid-forms.mp4', poster: '/tutorial-videos/cid-forms.jpg' },
    chapters: [
      { at: 19, title: 'Open CID Forms', text: 'On your phone: More → CID Forms.' },
      { at: 27, title: 'The list', text: 'Each document shows its type: PDF, DOCX (Word) or XLSX (Excel). Search by name or pick a category.' },
      { at: 57, title: 'A Word document', text: 'Tap Download. On an iPhone, choose Save to Files or open it in Word.' },
      { at: 83, title: 'A PDF', text: 'Tap View PDF to read it in the app, on your branch’s letterhead. Share sends it on WhatsApp or email, or saves it.' },
      { at: 112, title: 'An Excel sheet', text: 'Tap Download, the same as a Word document.' },
      { at: 125, title: 'On the web', text: 'CID Forms in the sidebar shows each document’s format and size. Open one and click Download.' },
      { at: 169, title: 'In short', text: 'Everything HQ shares with you is in CID Forms.' },
    ],
    note: 'Forms marked Fillable are filled in right in the app instead of downloaded.',
  },
  {
    id: 'earnings',
    topic: 'Earnings',
    title: 'See what you earn on every sale',
    summary: 'Your commission on every sale you close, how much is released and paid, and every payout HQ makes to you with its payment voucher. On your phone and on the web.',
    length: '3:43',
    added: '2026-10-27',
    video: { src: '/tutorial-videos/earnings.mp4', poster: '/tutorial-videos/earnings.jpg' },
    chapters: [
      { at: 11, title: 'Home', text: 'The Locked, Released and Paid tiles: the short version.' },
      { at: 26, title: 'Earnings', text: 'Tap Earnings at the bottom. Pick a period to see what was earned, paid and still pending; Total earning shows each month.' },
      { at: 46, title: 'My deals', text: 'Each sale with the commission on it. The tag says where the money is: Part released, Released (waiting for the next payout) or Paid.' },
      { at: 71, title: 'One deal', text: 'Tap a deal: the commission, how much is released, and how the deal splits. Open the sale for more.' },
      { at: 89, title: 'On the sale', text: 'The Commissions tab shows only your own commission: total, unlocked, paid. The Finance tab shows what the client has paid.' },
      { at: 118, title: 'Not invoiced yet', text: 'A sale without an invoice shows an estimate. It becomes final when HQ raises the invoice.' },
      { at: 133, title: 'Payouts', text: 'The Payout ledger shows what HQ still owes and the total paid. Tap a payout to see which sales it paid for and its payment voucher.' },
      { at: 176, title: 'Statement', text: 'A commission statement for any period: every sale, what was released and what was paid out.' },
      { at: 193, title: 'On the web', text: 'Earnings in the sidebar, everything on one page.' },
      { at: 211, title: 'In short', text: 'Every sale shows what you earn, every payout what was paid.' },
    ],
    note: 'A sale shows up under My deals once HQ raises its invoice. Before that, open the sale to see the estimate.',
  },
];

const NEW_FOR_DAYS = 30;
const dayOf = (t) => new Date(`${t.added}T00:00:00`);
export const isNew = (t, now = Date.now()) => now - dayOf(t).getTime() < NEW_FOR_DAYS * 86400000;
export const addedLabel = (t) => dayOf(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
export const findTutorial = (id) => TUTORIALS.find((t) => t.id === id) || null;

/** 75 → "1:15"; 3725 → "1:02:05". */
export function clock(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
const lengthSeconds = (t) => t.length.split(':').reduce((acc, n) => acc * 60 + Number(n), 0);
/** "11 min" for a list of tutorials. */
export const totalMinutes = (list) => `${Math.max(1, Math.round(list.reduce((n, t) => n + lengthSeconds(t), 0) / 60))} min`;

/** Topics in order of first appearance, each with its count. */
export const topicsOf = (list = TUTORIALS) => {
  const out = [];
  list.forEach((t) => {
    const hit = out.find((x) => x.topic === t.topic);
    if (hit) hit.count += 1; else out.push({ topic: t.topic, count: 1 });
  });
  return out;
};

/** Newest first. */
export const newestFirst = (list) => [...list].sort((a, b) => (a.added < b.added ? 1 : a.added > b.added ? -1 : 0));

/** Every word of the query must appear somewhere in the tutorial's text. */
export function matches(t, query) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [t.title, t.summary, t.topic, t.note, ...t.chapters.map((c) => `${c.title} ${c.text}`)]
    .filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** What to watch after `t`: the rest of its topic (newest first), then everything else. */
export function upNext(t, limit = 4) {
  const others = TUTORIALS.filter((x) => x.id !== t.id);
  return [
    ...newestFirst(others.filter((x) => x.topic === t.topic)),
    ...newestFirst(others.filter((x) => x.topic !== t.topic)),
  ].slice(0, limit);
}
